import { useEffect, useMemo, useState } from "react";
import Button from "@shared/v2/Button";
import Modal from "@shared/v2/Modal";
import NumberInput from "@shared/v2/NumberInput";
import TextButton from "@shared/v2/TextButton";
import Typography from "@shared/v2/Typography";
import Tooltip from "@shared/v2/Tooltip";
import { ArrowRightSolidV6, CircleInfoSolidV6 } from "@shared/v2/Icomoon";
import SaveProgressButton from "./SaveProgressButton";
import { useWizardContext } from "./WizardContext";

const ACTIVITY_FIELDS = [
  { key: "callsPerClosed", label: "Calls to Past Client, Sphere, and Referrals" },
  { key: "openHousesPerClosed", label: "Open Houses" },
  { key: "doorKnocksPerClosed", label: "Door Knocks" },
  { key: "callsCanceledPerClosed", label: "Calls to Canceled/Expired Leads" },
  { key: "callsFsboPerClosed", label: "Calls to FSBO Leads" },
  { key: "callsCirclePerClosed", label: "Calls to Circle Prospecting Leads" },
  { key: "callsWebSignTeamPerClosed", label: "Calls to Brivity/Web, Sign, and Team Leads" },
];

const createTeamDefaultStatsFromGoals = (goals = {}) =>
  Object.fromEntries(ACTIVITY_FIELDS.map(({ key }) => [key, String(goals[key] ?? 0)]));

const hasAnyNonZeroStat = (stats = {}) => ACTIVITY_FIELDS.some(({ key }) => Number(stats?.[key] ?? 0) !== 0);

const createStatsFromGoals = (goals = {}) => {
  const teamDefaults = createTeamDefaultStatsFromGoals(goals);
  const savedPersonalStats = goals.personalClosingConversionStats || {};
  const personalStats =
    hasAnyNonZeroStat(savedPersonalStats) || !hasAnyNonZeroStat(teamDefaults) ? savedPersonalStats : {};

  return Object.fromEntries(
    ACTIVITY_FIELDS.map(({ key }) => [key, String(personalStats[key] ?? teamDefaults[key])]),
  );
};

const areStatsEqual = (a = {}, b = {}) =>
  ACTIVITY_FIELDS.every(({ key }) => String(a[key] ?? "0") === String(b[key] ?? "0"));

const normalizeStatValue = (value) => {
  const cleanValue = String(value ?? "").replace(/[^0-9]/g, "");
  return cleanValue.length ? cleanValue : "0";
};

const ActivityPerClosed = () => {
  const {
    goToPage,
    year,
    saveProgress,
    wizardData: { goals, actions },
  } = useWizardContext();

  const [activityStats, setActivityStats] = useState(() => createStatsFromGoals(goals));
  const teamDefaultStats = useMemo(() => createTeamDefaultStatsFromGoals(goals), [goals]);

  useEffect(() => {
    setActivityStats(createStatsFromGoals(goals));
  }, [goals]);

  const resetDisabled = useMemo(
    () => areStatsEqual(activityStats, teamDefaultStats),
    [activityStats, teamDefaultStats],
  );

  const onStatChange = (key) => (e) => {
    setActivityStats((prev) => ({
      ...prev,
      [key]: normalizeStatValue(e.target.value),
    }));
  };

  const persistStats = ({ nextPage } = {}) => {
    const goalsToSave = {
      ...goals,
      personalClosingConversionStats: activityStats,
    };
    actions?.setLocalGoals(goalsToSave);
    saveProgress({ nextPage, nextGoals: goalsToSave });
  };

  return (
    <>
      <Modal.Header
        title={
          <>
            {`${year} Business Planning`} <span className="tw-text-12d tw-text-gray-50">Step 4 of 5</span>
          </>
        }
        closeButton
      />

      <Modal.Body className="tw-flex tw-flex-col tw-gap-[24px] tw-overflow-y-auto">
        <div className="tw-flex tw-flex-col tw-gap-[4px]">
          <Typography variant="subtitle-m-bold">
            How much of each activity below does it take you to close 1 unit with the corresponding source?
          </Typography>
          <Typography variant="body-s" className="!tw-text-gray-50">
            This will help us determine your conversion rates so we can tailor your Activity Goals towards
            your business. <br /> Feel free to adjust your stats from the defaults provided.
          </Typography>
        </div>
        <table>
          <thead>
            <tr className="[&>th]:tw-py-[8px] tw-text-12d tw-text-gray-50 tw-whitespace-nowrap tw-border-solid tw-border-[1px] tw-border-gray-30 tw-border-x-0">
              <th className="tw-pr-[4px] tw-font-semibold">Activity</th>
              <th className="tw-pl-[4px] tw-font-semibold tw-text-right">
                # to Close 1 Unit{" "}
                <Tooltip
                  trigger={<CircleInfoSolidV6 />}
                  content={
                    <>
                      The number of activities needed to close 1 unit in the corresponding source.
                      <br />
                      <br />
                      <i>Example: It takes me 20 open houses to close 1 unit with source “Open House”.</i>
                    </>
                  }
                  innerClassName="tw-max-w-[180px]"
                  multiline
                />
              </th>
            </tr>
          </thead>
          <tbody className="[&>tr:nth-child(even)]:tw-bg-gray-5">
            {ACTIVITY_FIELDS.map(({ key, label }) => (
              <tr key={key} className="[&>td]:tw-py-[4px] [&:first-child>td]:tw-pt-[8px]">
                <td className="tw-pr-[4px] tw-text-12d tw-text-gray-50 tw-font-semibold" valign="middle">
                  {label}
                </td>
                <td className="tw-pl-[4px]" align="right" valign="middle">
                  <div className="tw-inline-flex tw-max-w-[100px]">
                    <NumberInput
                      inputProps={{
                        className: "tw-min-w-[100px]",
                      }}
                      value={Number(activityStats[key]) || 0}
                      onChange={onStatChange(key)}
                      showControls={false}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal.Body>

      <Modal.Footer className="tw-flex tw-justify-between tw-gap-[8px]">
        <Button schema="tertiary" size="medium" onClick={() => goToPage(5)}>
          Back
        </Button>
        <div className="tw-flex tw-gap-[12px]">
          <TextButton onClick={() => setActivityStats(teamDefaultStats)} disabled={resetDisabled}>
            Reset Values
          </TextButton>
          <SaveProgressButton onClick={() => persistStats()} />
          <Button
            size="medium"
            className="tw-inline-flex tw-items-center tw-gap-[6px]"
            onClick={() => persistStats({ nextPage: 7 })}
          >
            Next <ArrowRightSolidV6 />
          </Button>
        </div>
      </Modal.Footer>
    </>
  );
};

export default ActivityPerClosed;
